import { SHORTCUT_CONST } from '@/constant';
import { getEnvValue, uuid } from '@/package';
import type { ObjectClass, ToolPermission } from '@/type';

import { TOOL_MENU_TYPE } from './constant';
import type { ToolMenuItem } from './type';

const SHORTCUT_COMMAND = SHORTCUT_CONST.SHORTCUT_COMMAND;

export const LABELING_LIST = getEnvValue('VITE_APP_LABELING_LIST', value =>
  value.split(','),
) as ('car' | 'truck' | 'pedestrian' | 'cyclist' | 'van' | 'bus')[];

export const createToolMenuItem = ({
  alwaysActived,
  children = [],
  command,
  disabled,
  group,
  hidden,
  isSelected,
  name,
  permission = {},
  type = TOOL_MENU_TYPE.DEFAULT,
}: SelectivePartial<
  Pick<
    ToolMenuItem,
    | 'alwaysActived'
    | 'children'
    | 'command'
    | 'disabled'
    | 'group'
    | 'hidden'
    | 'isSelected'
    | 'name'
    | 'permission'
    | 'type'
  >,
  'children' | 'command' | 'isSelected' | 'permission' | 'type'
>): ToolMenuItem => {
  const item: ToolMenuItem = {
    alwaysActived,
    children,
    command,
    disabled,
    group,
    hidden,
    id: uuid(),
    isSelected: isSelected ? true : alwaysActived || false,
    name,
    permission,
    type,
    get displayedToolMenu() {
      return item.children.length
        ? (item.children.find(child => child.isSelected) ?? item)
        : item;
    },
  };

  if (item.children.length > 0 && item.alwaysActived) {
    item.children[0].isSelected = true;
  }

  return item;
};

export const createInitBoxGenToolList = (
  superObjectClassList: ObjectClass[],
) => {
  return superObjectClassList.map(({ id, extendClass }, index) =>
    createToolMenuItem({
      name: `box${id}`,
      group: 'genBoxTool',
      command: (SHORTCUT_COMMAND as any)[
        `labeling.labelBox.generationMode${index + 1}`
      ],
      // NOTE: do not use switch type for extend box tool
      type: TOOL_MENU_TYPE.DEFAULT,
      children:
        extendClass?.map(({ id }) =>
          createToolMenuItem({
            name: `box${id}`,
            group: 'genExtendBoxTool',
          }),
        ) ?? [],
    }),
  );
};

export const createToolMenuList = (): ToolMenuItem[] => {
  return [
    /** AI Toolbar Group */
    createToolMenuItem({
      name: 'preAnnotation',
      command: SHORTCUT_COMMAND['labeling.toolMenu.preAnnotation'],
      group: 'editing',
      type: TOOL_MENU_TYPE.IMMEDIATELY,
      permission: {
        preAnnotation: {
          acceptable: true,
        },
        aiAssist: {
          acceptable: true,
        },
      },
    }),
    createToolMenuItem({
      name: 'aiAssist',
      command: SHORTCUT_COMMAND['labeling.toolMenu.aiAssist'],
      group: 'editing',
      type: TOOL_MENU_TYPE.TOGGLE,
      permission: {
        preAnnotation: {
          acceptable: true,
        },
        aiAssist: {
          acceptable: true,
        },
      },
    }),

    /** Working Toolbar Group */
    createToolMenuItem({
      name: 'select',
      command: SHORTCUT_COMMAND['labeling.labelBox.select'],
      group: 'editing',
      type: TOOL_MENU_TYPE.DEFAULT,
    }),
    createToolMenuItem({
      name: 'box',
      command: SHORTCUT_COMMAND['labeling.labelBox.generationMode'],
      children: [],
      type: TOOL_MENU_TYPE.DEFAULT,
      group: 'editing',
      permission: {
        labeling: {
          updatable: true,
        },
        labelingReview: {
          updatable: true,
        },
      },
    }),
    createToolMenuItem({
      name: 'selectAll',
      command: SHORTCUT_COMMAND['labeling.labelBox.selectAll'],
      group: 'editing',
      type: TOOL_MENU_TYPE.IMMEDIATELY,
      hidden: true,
    }),

    createToolMenuItem({
      name: 'boxValidation',
      group: 'leftPopup',
      command: SHORTCUT_COMMAND['labeling.boxValidation.toggle'],
      type: TOOL_MENU_TYPE.TOGGLE,
    }),
    createToolMenuItem({
      name: 'reviewData',
      group: 'leftPopup',
      command: SHORTCUT_COMMAND['labeling.reviewData.toggle'],
      type: TOOL_MENU_TYPE.TOGGLE,
    }),

    /** History Toolbar Group */
    createToolMenuItem({
      name: 'redo',
      command: SHORTCUT_COMMAND['labeling.history.redo'],
      group: 'history',
      type: TOOL_MENU_TYPE.IMMEDIATELY,
      permission: {
        labeling: {
          updatable: true,
        },
        labelingReview: {
          updatable: true,
        },
      },
    }),
    createToolMenuItem({
      name: 'undo',
      command: SHORTCUT_COMMAND['labeling.history.undo'],
      group: 'history',
      type: TOOL_MENU_TYPE.IMMEDIATELY,
      permission: {
        labeling: {
          updatable: true,
        },
        labelingReview: {
          updatable: true,
        },
      },
    }),
    createToolMenuItem({
      name: 'revert',
      command: SHORTCUT_COMMAND['labeling.history.revert'],
      group: 'history',
      type: TOOL_MENU_TYPE.IMMEDIATELY,
      permission: {
        labeling: {
          updatable: true,
        },
        labelingReview: {
          updatable: true,
        },
      },
    }),

    /** Camera Toolbar Group */
    createToolMenuItem({
      name: 'viewMode',
      command: SHORTCUT_COMMAND['labeling.screen.changeView'],
      group: 'screenView',
      type: TOOL_MENU_TYPE.SWITCH,
      alwaysActived: true,
      children: [
        createToolMenuItem({ name: 'viewModeDefault', group: 'screenView' }),
        createToolMenuItem({ name: 'viewModeTop', group: 'screenView' }),
        createToolMenuItem({ name: 'viewModeSide', group: 'screenView' }),
        createToolMenuItem({ name: 'viewModeFront', group: 'screenView' }),
      ],
    }),
    createToolMenuItem({
      name: 'fullScreen',
      command: SHORTCUT_COMMAND['labeling.screen.fullScreen'],
      group: 'fullScreen',
      type: TOOL_MENU_TYPE.TOGGLE,
    }),

    /** Setting Toolbar Group */
    createToolMenuItem({
      name: 'settings',
      group: 'leftPopup',
      command: SHORTCUT_COMMAND['labeling.settingPopup.toggle'],
      type: TOOL_MENU_TYPE.TOGGLE,
    }),
    createToolMenuItem({
      name: 'frameCamImage',
      group: 'leftPopup',
      command: SHORTCUT_COMMAND['labeling.frameCamImagePopup.toggle'],
      type: TOOL_MENU_TYPE.TOGGLE,
      // children: [
      //   createToolMenuItem({
      //     name: 'frameCamImageHide',
      //     group: 'frameCamImage',
      //   }),
      //   createToolMenuItem({
      //     name: 'frameCamImageShow',
      //     group: 'frameCamImage',
      //   }),
      // ],
    }),
    createToolMenuItem({
      name: 'syncSaving',
      command: SHORTCUT_COMMAND['labeling.sync.saving'],
      group: 'sync',
    }),
    createToolMenuItem({
      name: 'syncSaved',
      command: SHORTCUT_COMMAND['labeling.sync.saved'],
      group: 'sync',
    }),

    /** Hidden tool */
    createToolMenuItem({
      name: 'grid',
      command: SHORTCUT_COMMAND['labeling.grid.toggle'],
      group: 'grid',
      type: TOOL_MENU_TYPE.SWITCH,
      hidden: true,
      isSelected: true,
      children: [
        createToolMenuItem({
          name: 'gridShow',
          group: 'grid',
          isSelected: true,
        }),
        createToolMenuItem({
          name: 'gridHide',
          group: 'grid',
        }),
      ],
    }),

    // createToolMenuItem({
    //   name: 'cameraPannel',
    //   command: SHORTCUT_COMMAND['labeling.cameraPannel.toggle'],
    //   group: 'cameraPannel',
    //   type: TOOL_MENU_TYPE.TOGGLE,
    //   hidden: true,
    //   // children: [
    //   //   createToolMenuItem({
    //   //     name: 'labelBoxArrowShow',
    //   //     group: 'labelBoxArrow',
    //   //     isSelected: true,
    //   //   }),
    //   //   createToolMenuItem({
    //   //     name: 'labelBoxArrowHide',
    //   //     group: 'labelBoxArrow',
    //   //   }),
    //   // ],
    // }),
    createToolMenuItem({
      name: 'deselectAllToolMenu',
      command: SHORTCUT_COMMAND['labeling.toolMenu.deselectAll'],
      group: 'deselectAllToolMenu',
      type: TOOL_MENU_TYPE.IMMEDIATELY,
      hidden: true,
    }),
    createToolMenuItem({
      name: 'labelBoxCopy',
      command: SHORTCUT_COMMAND['labeling.labelBox.copyBox'],
      group: 'labelBoxCopyPaste',
      type: TOOL_MENU_TYPE.IMMEDIATELY,
      hidden: true,
    }),
    createToolMenuItem({
      name: 'labelBoxPaste',
      command: SHORTCUT_COMMAND['labeling.labelBox.pasteBox'],
      group: 'labelBoxCopyPaste',
      type: TOOL_MENU_TYPE.IMMEDIATELY,
      hidden: true,
      permission: {
        labeling: {
          updatable: true,
        },
        labelingReview: {
          updatable: true,
        },
      },
    }),
    createToolMenuItem({
      name: 'labelBoxCut',
      command: SHORTCUT_COMMAND['labeling.labelBox.cutBox'],
      group: 'labelBoxCopyPaste',
      type: TOOL_MENU_TYPE.IMMEDIATELY,
      hidden: true,
      permission: {
        labeling: {
          updatable: true,
        },
        labelingReview: {
          updatable: true,
        },
      },
    }),
    createToolMenuItem({
      name: 'labelBoxRotate90',
      command: SHORTCUT_COMMAND['labeling.labelBox.rotate90Degree'],
      group: 'labelBoxRotate',
      type: TOOL_MENU_TYPE.IMMEDIATELY,
      hidden: true,
      permission: {
        labeling: {
          updatable: true,
        },
      },
    }),
    createToolMenuItem({
      name: 'labelBoxRotate180',
      command: SHORTCUT_COMMAND['labeling.labelBox.rotate180Degree'],
      group: 'labelBoxRotate',
      type: TOOL_MENU_TYPE.IMMEDIATELY,
      hidden: true,
      permission: {
        labeling: {
          updatable: true,
        },
        labelingReview: {
          updatable: true,
        },
      },
    }),
    createToolMenuItem({
      name: 'labelBoxRotateClockwise',
      command: SHORTCUT_COMMAND['labeling.labelBox.rotateClockwise'],
      group: 'labelBoxRotate',
      type: TOOL_MENU_TYPE.IMMEDIATELY,
      hidden: true,
      permission: {
        labeling: {
          updatable: true,
        },
        labelingReview: {
          updatable: true,
        },
      },
    }),
    createToolMenuItem({
      name: 'labelBoxRotateCounterClockwise',
      command: SHORTCUT_COMMAND['labeling.labelBox.rotateCounterClockwise'],
      group: 'labelBoxRotate',
      type: TOOL_MENU_TYPE.IMMEDIATELY,
      hidden: true,
      permission: {
        labeling: {
          updatable: true,
        },
        labelingReview: {
          updatable: true,
        },
      },
    }),
    createToolMenuItem({
      name: 'labelBoxMove+Z',
      command: SHORTCUT_COMMAND['labeling.labelBox.move+Z'],
      group: 'labelBoxMove',
      type: TOOL_MENU_TYPE.IMMEDIATELY,
      hidden: true,
      permission: {
        labeling: {
          updatable: true,
        },
        labelingReview: {
          updatable: true,
        },
      },
    }),
    createToolMenuItem({
      name: 'labelBoxMove-Z',
      command: SHORTCUT_COMMAND['labeling.labelBox.move-Z'],
      group: 'labelBoxMove',
      type: TOOL_MENU_TYPE.IMMEDIATELY,
      hidden: true,
      permission: {
        labeling: {
          updatable: true,
        },
        labelingReview: {
          updatable: true,
        },
      },
    }),
    createToolMenuItem({
      name: 'labelBoxMove+X',
      command: SHORTCUT_COMMAND['labeling.labelBox.move+X'],
      group: 'labelBoxMove',
      type: TOOL_MENU_TYPE.IMMEDIATELY,
      hidden: true,
      permission: {
        labeling: {
          updatable: true,
        },
        labelingReview: {
          updatable: true,
        },
      },
    }),
    createToolMenuItem({
      name: 'labelBoxMove-X',
      command: SHORTCUT_COMMAND['labeling.labelBox.move-X'],
      group: 'labelBoxMove',
      type: TOOL_MENU_TYPE.IMMEDIATELY,
      hidden: true,
      permission: {
        labeling: {
          updatable: true,
        },
        labelingReview: {
          updatable: true,
        },
      },
    }),
    createToolMenuItem({
      name: 'labelBoxMove+Y',
      command: SHORTCUT_COMMAND['labeling.labelBox.move+Y'],
      group: 'labelBoxMove',
      type: TOOL_MENU_TYPE.IMMEDIATELY,
      hidden: true,
      permission: {
        labeling: {
          updatable: true,
        },
        labelingReview: {
          updatable: true,
        },
      },
    }),
    createToolMenuItem({
      name: 'labelBoxMove-Y',
      command: SHORTCUT_COMMAND['labeling.labelBox.move-Y'],
      group: 'labelBoxMove',
      type: TOOL_MENU_TYPE.IMMEDIATELY,
      hidden: true,
      permission: {
        labeling: {
          updatable: true,
        },
        labelingReview: {
          updatable: true,
        },
      },
    }),
    createToolMenuItem({
      name: 'labelBoxDecreaseHeight',
      command: SHORTCUT_COMMAND['labeling.labelBox.decreaseHeight'],
      group: 'labelBoxDimension',
      type: TOOL_MENU_TYPE.IMMEDIATELY,
      hidden: true,
      permission: {
        labeling: {
          updatable: true,
        },
        labelingReview: {
          updatable: true,
        },
      },
    }),
    createToolMenuItem({
      name: 'labelBoxIncreaseHeight',
      command: SHORTCUT_COMMAND['labeling.labelBox.increaseHeight'],
      group: 'labelBoxDimension',
      type: TOOL_MENU_TYPE.IMMEDIATELY,
      hidden: true,
      permission: {
        labeling: {
          updatable: true,
        },
        labelingReview: {
          updatable: true,
        },
      },
    }),
    createToolMenuItem({
      name: 'labelBoxDecreaseDepth',
      command: SHORTCUT_COMMAND['labeling.labelBox.decreaseDepth'],
      group: 'labelBoxDimension',
      type: TOOL_MENU_TYPE.IMMEDIATELY,
      hidden: true,
      permission: {
        labeling: {
          updatable: true,
        },
        labelingReview: {
          updatable: true,
        },
      },
    }),
    createToolMenuItem({
      name: 'labelBoxIncreaseDepth',
      command: SHORTCUT_COMMAND['labeling.labelBox.increaseDepth'],
      group: 'labelBoxDimension',
      type: TOOL_MENU_TYPE.IMMEDIATELY,
      hidden: true,
      permission: {
        labeling: {
          updatable: true,
        },
        labelingReview: {
          updatable: true,
        },
      },
    }),
    createToolMenuItem({
      name: 'labelBoxRemove',
      command: SHORTCUT_COMMAND['labeling.labelBox.removeBox'],
      group: 'labelBoxRemove',
      type: TOOL_MENU_TYPE.IMMEDIATELY,
      hidden: true,
      permission: {
        labeling: {
          updatable: true,
        },
        labelingReview: {
          updatable: true,
        },
      },
    }),
    createToolMenuItem({
      name: 'labelBoxArrow',
      command: SHORTCUT_COMMAND['labeling.labelBox.arrowToggle'],
      group: 'labelBoxArrow',
      type: TOOL_MENU_TYPE.TOGGLE,
      hidden: true,
    }),
    createToolMenuItem({
      name: 'labelBoxTrackID',
      command: SHORTCUT_COMMAND['labeling.labelBox.trackIDToggle'],
      group: 'labelBoxTrackID',
      type: TOOL_MENU_TYPE.TOGGLE,
      hidden: true,
    }),
    createToolMenuItem({
      name: 'isShortTrackID',
      command: SHORTCUT_COMMAND['labeling.labelBox.isShortTrackID'],
      group: 'isShortTrackID',
      type: TOOL_MENU_TYPE.TOGGLE,
      hidden: true,
    }),
    createToolMenuItem({
      name: 'pcdPointColor',
      group: 'pcdPointColor',
      type: TOOL_MENU_TYPE.TOGGLE,
      hidden: true,
      isSelected: true,
    }),
    createToolMenuItem({
      name: 'moveNextFrame',
      command: SHORTCUT_COMMAND['labeling.frame.nextFrame'],
      group: 'frame',
      type: TOOL_MENU_TYPE.IMMEDIATELY,
      hidden: true,
    }),
    createToolMenuItem({
      name: 'movePrevFrame',
      command: SHORTCUT_COMMAND['labeling.frame.prevFrame'],
      group: 'frame',
      type: TOOL_MENU_TYPE.IMMEDIATELY,
      hidden: true,
    }),
    createToolMenuItem({
      name: 'moveFirstFrame',
      command: SHORTCUT_COMMAND['labeling.frame.goFirst'],
      group: 'frame',
      type: TOOL_MENU_TYPE.IMMEDIATELY,
      hidden: true,
    }),
    createToolMenuItem({
      name: 'moveLastFrame',
      command: SHORTCUT_COMMAND['labeling.frame.goLast'],
      group: 'frame',
      type: TOOL_MENU_TYPE.IMMEDIATELY,
      hidden: true,
    }),
    createToolMenuItem({
      name: 'playStopFrame',
      command: SHORTCUT_COMMAND['labeling.frame.playOrStop'],
      group: 'playStopFrame',
      type: TOOL_MENU_TYPE.SWITCH,
      hidden: true,
      children: [
        createToolMenuItem({
          name: 'playFrame',
          group: 'playStopFrame',
        }),
        createToolMenuItem({
          name: 'stopFrame',
          group: 'playStopFrame',
        }),
      ],
    }),
    createToolMenuItem({
      name: 'transformDimension',
      command: SHORTCUT_COMMAND['labeling.labelBox.transformDimension'],
      group: 'transform',
      type: 'toggle',
      hidden: true,
      permission: {
        labeling: {
          updatable: true,
        },
        labelingReview: {
          updatable: true,
        },
      },
    }),
    createToolMenuItem({
      name: 'shortcutPopup',
      command: SHORTCUT_COMMAND['labeling.shortcutPopup.toggle'],
      group: 'shortcutPopup',
      type: TOOL_MENU_TYPE.TOGGLE,
      hidden: true,
    }),
    createToolMenuItem({
      name: 'transformPosition',
      command: SHORTCUT_COMMAND['labeling.labelBox.transformPosition'],
      group: 'transform',
      type: 'toggle',
      hidden: true,
      permission: {
        labeling: {
          updatable: true,
        },
        labelingReview: {
          updatable: true,
        },
      },
    }),
    createToolMenuItem({
      name: 'transformRotation',
      command: SHORTCUT_COMMAND['labeling.labelBox.transformRotation'],
      group: 'transform',
      type: 'toggle',
      hidden: true,
      permission: {
        labeling: {
          updatable: true,
        },
        labelingReview: {
          updatable: true,
        },
      },
    }),

    /** Track ID Toolbar Group */
    createToolMenuItem({
      name: 'searchTrackID',
      command: SHORTCUT_COMMAND['labeling.labelBox.trackIDSearch'],
      group: 'leftPopup',
      type: TOOL_MENU_TYPE.TOGGLE,
      permission: {},
    }),
    createToolMenuItem({
      name: 'copyTrackID',
      command: SHORTCUT_COMMAND['labeling.labelBox.trackIDCopy'],
      group: 'trackIDClipboard',
      type: TOOL_MENU_TYPE.IMMEDIATELY,
    }),
    createToolMenuItem({
      name: 'pasteTrackID',
      command: SHORTCUT_COMMAND['labeling.labelBox.trackIDPaste'],
      group: 'trackIDClipboard',
      type: TOOL_MENU_TYPE.IMMEDIATELY,
      permission: {
        labeling: {
          updatable: true,
        },
        labelingReview: {
          updatable: true,
        },
      },
    }),
    createToolMenuItem({
      name: 'updateTrackID',
      command: SHORTCUT_COMMAND['labeling.labelBox.trackIDUpdate'],
      group: 'trackIDUpdate',
      type: TOOL_MENU_TYPE.IMMEDIATELY,
      permission: {
        labeling: {
          updatable: true,
        },
        labelingReview: {
          updatable: true,
        },
      },
    }),

    /** Focus Box Toolbar Group */
    createToolMenuItem({
      name: 'focusPrevBox',
      command: SHORTCUT_COMMAND['labeling.labelBox.focusPrevBox'],
      group: 'focusBox',
      type: TOOL_MENU_TYPE.IMMEDIATELY,
    }),
    createToolMenuItem({
      name: 'focusNextBox',
      command: SHORTCUT_COMMAND['labeling.labelBox.focusNextBox'],
      group: 'focusBox',
      type: TOOL_MENU_TYPE.IMMEDIATELY,
    }),
  ];
};

export const createToolMap = (toolList: ToolMenuItem[]) => {
  const map: Record<string, ToolMenuItem> = {};

  const addToMap = (itemList: ToolMenuItem[]) => {
    itemList.forEach(item => {
      map[item.id] = item;
      item.children.length && addToMap(item.children);
    });
  };

  addToMap(toolList);

  return map;
};

export const checkToolPermission = (
  tool: ToolMenuItem,
  permission: ToolPermission,
) => {
  if (!tool.permission) return true;

  for (const scope of Object.keys(tool.permission) as Array<
    keyof ToolPermission
  >) {
    const scopePermission = tool.permission[scope];
    if (!scopePermission) continue;

    for (const name of Object.keys(scopePermission)) {
      const toolPermValue =
        scopePermission[name as keyof typeof scopePermission];
      const permissionValue =
        permission[scope][name as keyof (typeof permission)[typeof scope]];

      if (
        (scope === 'labeling' &&
          name === 'updatable' &&
          (permissionValue || permission['labelingReview']?.updatable)) ||
        (scope === 'labelingReview' &&
          name === 'updatable' &&
          (permissionValue || permission['labeling']?.updatable))
      )
        continue;

      if (toolPermValue !== permissionValue) return false;
    }
  }

  return true;
};

export const selectTool = (
  toolList: ToolMenuItem[],
  toolId: string,
  isExclusive = true,
) => {
  const selectedTool = toolList.find(tool => tool.id === toolId);

  if (!selectedTool) return;

  const selectedGroup = selectedTool.group;

  if (selectedTool.type === TOOL_MENU_TYPE.TOGGLE) {
    if (selectedTool.isSelected) {
      selectedTool.isSelected = false;
      return;
    }
  }

  toolList.forEach(tool => {
    if (tool.id === toolId) {
      tool.isSelected = true;
    } else if (
      isExclusive &&
      !tool.alwaysActived &&
      tool.group === selectedGroup
    ) {
      tool.isSelected = false;
    }

    // tool.children.length && selectTool(tool.children, toolId, false);
  });
};

export const deselectTool = (toolList: ToolMenuItem[], toolId: string) => {
  toolList.forEach(tool => {
    if (tool.id === toolId && !tool.alwaysActived) {
      tool.isSelected = false;
    }

    tool.children.length && deselectTool(tool.children, toolId);
  });
};

export const setDisabledTool = (
  toolList: ToolMenuItem[],
  toolId: string,
  disabled: boolean,
) => {
  toolList.forEach(tool => {
    if (tool.id === toolId && !tool.alwaysActived) {
      tool.disabled = disabled;
    }

    tool.children.length && setDisabledTool(tool.children, toolId, disabled);
  });
};

export const selectChildTool = (
  toolList: ToolMenuItem[],
  parentId: string,
  childId: string,
) => {
  toolList.some(tool => {
    if (tool.id === parentId) {
      tool.children.forEach(child => {
        child.isSelected = child.id === childId;
      });

      return true;
    }

    tool.children.length && selectChildTool(tool.children, parentId, childId);
  });
};

export const findSelectedToolList = (
  toolList: ToolMenuItem[],
): ToolMenuItem[] => {
  const result: ToolMenuItem[] = [];

  toolList.forEach(tool => {
    tool.isSelected && result.push(tool);
    tool.children.length && result.push(...findSelectedToolList(tool.children));
  });

  return result;
};
